import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACTIVE_GRAB_STATUSES, QUALITY_LABELS, type GrabDTO } from '@jelly-dl/shared';
import { api } from '../api/client';
import { Badge, Button, Card, ErrorState, ProgressBar, Spinner } from '../components/ui';
import { GRAB_STATUS_COLORS, QUALITY_COLORS, humanBytes, timeAgo } from '../lib/format';

const ACTIVE_STATUSES: string[] = ACTIVE_GRAB_STATUSES;

interface DownloaderStatus {
  downloadPaused: boolean;
  serverStandBy: boolean;
  downloadRate: number;
  freeDiskSpaceMB: number;
}

// Only movie grabs map cleanly to a detail page (season/episode targetIds aren't series ids).
function GrabLabel({ g, className }: { g: GrabDTO; className?: string }) {
  if (g.targetType === 'movie' && g.targetId) {
    return (
      <Link to={`/movie/${g.targetId}`} className={className}>
        {g.title}
      </Link>
    );
  }
  return <span className={className}>{g.title}</span>;
}

export function Activity() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['queue'],
    queryFn: () => api.get<{ items: GrabDTO[]; downloader: DownloaderStatus | null }>('/api/queue'),
    // Poll fast while something is active; back off to 15s when the queue is idle.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((g) => ACTIVE_STATUSES.includes(g.status)) ? 3_000 : 15_000,
  });

  const cancel = useMutation({
    mutationFn: (gid: string) => api.del(`/api/queue/${gid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['queue'] }),
  });
  const retry = useMutation({
    mutationFn: (gid: string) => api.post(`/api/queue/${gid}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['queue'] }),
  });

  if (isLoading) return <Spinner />;
  if (isError) return <ErrorState message="Couldn't load the queue." onRetry={() => refetch()} />;
  const items = data?.items ?? [];
  const active = items.filter((g) => ACTIVE_STATUSES.includes(g.status));
  const done = items.filter((g) => !active.includes(g));
  const paused = data?.downloader?.downloadPaused ?? false;

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold">Activity</h1>

      {paused && (
        <Card className="border-amber-500/40 bg-amber-500/10 text-sm text-amber-200">
          Downloads are paused on NZBGet
          {data?.downloader ? ` — ${(data.downloader.freeDiskSpaceMB / 1024).toFixed(1)} GB free on the downloads volume` : ''}.
          They auto-resume once there's enough disk space; free up space if this persists.
        </Card>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-300">Active ({active.length})</h2>
        {active.length === 0 && <p className="text-sm text-slate-500">Nothing downloading right now.</p>}
        {active.map((g) => (
          <Card key={g.id} className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <GrabLabel g={g} className="truncate font-medium hover:text-indigo-300" />
                <Badge className={QUALITY_COLORS[g.qualityKey]}>{QUALITY_LABELS[g.qualityKey]}</Badge>
                {g.isSeasonPack && <Badge className="bg-indigo-600/20 text-indigo-200">Pack</Badge>}
              </div>
              <span className={`text-sm ${GRAB_STATUS_COLORS[g.status]}`}>{g.status}</span>
            </div>
            <ProgressBar permille={g.progressPermille} />
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>
                {humanBytes(g.downloadedBytes)} / {humanBytes(g.sizeBytes)} · {g.nzbgetStatus ?? '—'}
              </span>
              <Button
                variant="ghost"
                disabled={cancel.isPending && cancel.variables === g.id}
                onClick={() => cancel.mutate(g.id)}
              >
                Cancel
              </Button>
            </div>
          </Card>
        ))}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-300">Recent</h2>
        {done.length === 0 && <p className="text-sm text-slate-500">No recent downloads.</p>}
        {done.map((g) => (
          <Card key={g.id} className="flex items-center justify-between py-2 text-sm">
            <div className="min-w-0">
              <GrabLabel g={g} className="hover:text-indigo-300" />
              <div className="text-xs text-slate-500">
                {g.nzbName} · {timeAgo(g.completedAt ?? g.grabbedAt)}
                {g.error ? ` · ${g.error}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={GRAB_STATUS_COLORS[g.status]}>{g.status}</span>
              {(g.status === 'failed' || g.status === 'cancelled') && (
                <Button
                  variant="ghost"
                  disabled={retry.isPending && retry.variables === g.id}
                  onClick={() => retry.mutate(g.id)}
                >
                  Retry
                </Button>
              )}
            </div>
          </Card>
        ))}
      </section>
    </div>
  );
}
